import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Component to safely render HTML/JS ad code
const EmbedAdRenderer = ({ code, className = '' }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!code || !containerRef.current) return;

    // Clear previous content
    containerRef.current.innerHTML = '';

    // Create a container for the ad code
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = code;

    // Extract and execute scripts
    const scripts = tempDiv.querySelectorAll('script');
    const nonScriptContent = code.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    
    // Add non-script content first
    containerRef.current.innerHTML = nonScriptContent;

    // Execute scripts
    scripts.forEach((oldScript) => {
      const newScript = document.createElement('script');
      
      // Copy attributes
      Array.from(oldScript.attributes).forEach((attr) => {
        newScript.setAttribute(attr.name, attr.value);
      });
      
      // Copy inline script content
      if (oldScript.innerHTML) {
        newScript.innerHTML = oldScript.innerHTML;
      }
      
      containerRef.current.appendChild(newScript);
    });

    // Cleanup function
    return () => {
      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
    };
  }, [code]);

  return <div ref={containerRef} className={className} />;
};

const BannerAd = ({ position = 'sidebar', page = 'all', className = '' }) => {
  const [ads, setAds] = useState([]);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    fetchAds();
  }, [position, page]);

  // Rotate ads every 10 seconds if multiple
  useEffect(() => {
    if (ads.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentAdIndex(prev => (prev + 1) % ads.length);
    }, 10000);
    
    return () => clearInterval(interval);
  }, [ads.length]);

  const fetchAds = async () => {
    try {
      const response = await axios.get(`${API}/ads/active`, {
        params: { position, page }
      });
      setAds(response.data.ads || []);
    } catch (error) {
      console.error('Error fetching ads:', error);
    }
  };

  const trackImpression = async (adId) => {
    try {
      await axios.post(`${API}/ads/${adId}/impression`);
    } catch (error) {
      // Silent fail
    }
  };

  const trackClick = async (adId) => {
    try {
      await axios.post(`${API}/ads/${adId}/click`);
    } catch (error) {
      // Silent fail
    }
  };

  useEffect(() => {
    if (ads.length > 0 && ads[currentAdIndex]) {
      trackImpression(ads[currentAdIndex].id);
    }
  }, [currentAdIndex, ads]);

  if (dismissed || ads.length === 0) return null;

  const currentAd = ads[currentAdIndex];
  if (!currentAd) return null;

  const handleClick = () => {
    trackClick(currentAd.id);
  };

  const isEmbedAd = currentAd.ad_type === 'code' && currentAd.embed_code;

  // Render embed/code ad
  const renderEmbedAd = () => (
    <div className="ad-embed-container" data-testid={`banner-ad-embed-${position}`}>
      <div className="text-center text-xs text-gray-500 mb-1">Advertisement</div>
      <EmbedAdRenderer code={currentAd.embed_code} className="ad-content" />
    </div>
  );

  // Render image ad
  const renderImageAd = () => (
    <a
      href={currentAd.link_url}
      target="_blank"
      rel="noopener noreferrer sponsored"
      onClick={handleClick}
      className="block"
    >
      <img
        src={currentAd.image_url}
        alt={currentAd.alt_text || currentAd.title}
        className="w-full h-auto"
      />
    </a>
  );

  // Different layouts based on position
  if (position === 'header') {
    return (
      <div className={`relative bg-gray-800 border-b border-gray-700 ${className}`} data-testid="banner-ad-header">
        <div className="container mx-auto px-4 py-2">
          {isEmbedAd ? (
            renderEmbedAd()
          ) : (
            <a
              href={currentAd.link_url}
              target="_blank"
              rel="noopener noreferrer sponsored"
              onClick={handleClick}
              className="flex items-center justify-center gap-4"
            >
              <img
                src={currentAd.image_url}
                alt={currentAd.alt_text || currentAd.title}
                className="h-12 md:h-16 object-contain"
              />
              <span className="text-xs text-gray-500">Ad</span>
            </a>
          )}
          <button
            onClick={() => setDismissed(true)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white p-1"
            aria-label="Dismiss ad"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  if (position === 'sidebar') {
    return (
      <div className={`relative bg-gray-800 border border-gray-700 rounded-lg overflow-hidden ${className}`} data-testid="banner-ad-sidebar">
        {isEmbedAd ? (
          <div className="p-2">
            {renderEmbedAd()}
          </div>
        ) : (
          <>
            {renderImageAd()}
            <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-1.5 py-0.5 rounded">
              Ad
            </div>
          </>
        )}
        {ads.length > 1 && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
            {ads.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentAdIndex(i)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  i === currentAdIndex ? 'bg-orange-500' : 'bg-gray-500'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  if (position === 'between_content') {
    return (
      <div className={`relative my-8 ${className}`} data-testid="banner-ad-content">
        {isEmbedAd ? (
          <div className="max-w-3xl mx-auto">
            {renderEmbedAd()}
          </div>
        ) : (
          <>
            <div className="text-center text-xs text-gray-500 mb-2">Advertisement</div>
            <a
              href={currentAd.link_url}
              target="_blank"
              rel="noopener noreferrer sponsored"
              onClick={handleClick}
              className="block mx-auto max-w-3xl"
            >
              <img
                src={currentAd.image_url}
                alt={currentAd.alt_text || currentAd.title}
                className="w-full h-auto rounded-lg border border-gray-700"
              />
            </a>
          </>
        )}
      </div>
    );
  }

  if (position === 'footer') {
    return (
      <div className={`bg-gray-800 border-t border-gray-700 py-4 ${className}`} data-testid="banner-ad-footer">
        <div className="container mx-auto px-4">
          {isEmbedAd ? (
            <div className="max-w-2xl mx-auto">
              {renderEmbedAd()}
            </div>
          ) : (
            <>
              <div className="text-center text-xs text-gray-500 mb-2">Sponsored</div>
              <a
                href={currentAd.link_url}
                target="_blank"
                rel="noopener noreferrer sponsored"
                onClick={handleClick}
                className="block mx-auto max-w-2xl"
              >
                <img
                  src={currentAd.image_url}
                  alt={currentAd.alt_text || currentAd.title}
                  className="w-full h-auto rounded"
                />
              </a>
            </>
          )}
        </div>
      </div>
    );
  }

  if (position === 'popup') {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="banner-ad-popup">
        <div className="relative bg-gray-900 border border-gray-700 rounded-xl overflow-hidden max-w-lg w-full">
          <button
            onClick={() => setDismissed(true)}
            className="absolute top-2 right-2 text-gray-400 hover:text-white bg-black/50 rounded-full p-1 z-10"
            aria-label="Close ad"
          >
            <X className="h-5 w-5" />
          </button>
          {isEmbedAd ? (
            <div className="p-4">
              {renderEmbedAd()}
            </div>
          ) : (
            <>
              <a
                href={currentAd.link_url}
                target="_blank"
                rel="noopener noreferrer sponsored"
                onClick={handleClick}
                className="block"
              >
                <img
                  src={currentAd.image_url}
                  alt={currentAd.alt_text || currentAd.title}
                  className="w-full h-auto"
                />
              </a>
              <div className="p-4 text-center">
                <span className="text-xs text-gray-500">Sponsored content</span>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return null;
};

export default BannerAd;
